import { motion, useInView } from "motion/react";
import { useRef } from "react";
import { BRAND } from "../../lib/brand";
import { ENGAGEMENT_MODELS, LANGUAGES, PAYROLL_ENGAGEMENT } from "../../data/profileData";
import { CvFinder } from "./CvFinder";

const MALT_URL = "https://en.malt.nl/profile/olgamolina";

const ACCENTS = [BRAND.red, BRAND.blue, BRAND.teal];

export function EngagementSection() {
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section ref={ref} className="relative w-full px-4 md:px-[116px] py-16 md:py-24">
      <div className="max-w-[1400px] mx-auto flex flex-col gap-10">
        <div className="flex flex-col gap-4">
          <p className="font-mono text-[12px] md:text-[14px] tracking-[3px] uppercase text-[#666]">How to hire me</p>
          <h2 className="font-black text-[36px] md:text-[64px] leading-[1.05] tracking-tight text-[#141414]">
            Four ways to work together
          </h2>
        </div>

        {/* Featured: the permanent, best-value hire — sits above the temporary three. */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="relative flex flex-col gap-4 overflow-hidden rounded-[28px] border-2 border-[#141414] p-7 md:p-10 shadow-[0px_18px_40px_-14px_rgba(0,62,91,0.4)]"
          style={{ background: BRAND.bg }}
        >
          <span className="inline-flex w-fit items-center gap-2 rounded-full border-2 border-[#141414] bg-[#141414] px-4 py-1.5 text-[12px] font-bold uppercase tracking-[2px] text-white">
            <span aria-hidden="true">★</span> {PAYROLL_ENGAGEMENT.badge}
          </span>
          <div className="flex flex-col gap-3 md:max-w-[820px]">
            <h3 className="font-black text-[30px] md:text-[40px] leading-tight text-[#003e5b]">
              {PAYROLL_ENGAGEMENT.title}
            </h3>
            <p className="text-[16px] md:text-[18px] leading-[1.6] text-[#003e5b]">{PAYROLL_ENGAGEMENT.body}</p>
          </div>
          <div className="mt-1">
            <CvFinder />
          </div>
        </motion.div>

        <div className="grid gap-4 md:grid-cols-3">
          {ENGAGEMENT_MODELS.map((model, i) => (
            <motion.div
              key={model.title}
              initial={{ opacity: 0, y: 24 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: i * 0.1, ease: "easeOut" }}
              className="flex flex-col gap-3 rounded-[24px] bg-white p-6 md:p-7 shadow-[0px_10px_24px_-12px_rgba(0,62,91,0.35)]"
            >
              <span className="h-[6px] w-[48px] rounded-full" style={{ background: ACCENTS[i] }} aria-hidden="true" />
              <h3 className="font-black text-[24px] leading-tight text-[#141414]">{model.title}</h3>
              <p className="text-[15px] leading-[1.6] text-[#666]">{model.body}</p>
            </motion.div>
          ))}
        </div>

        <p className="text-[15px] text-[#666]">
          Prefer to hire through a platform? Find me on{" "}
          <a
            href={MALT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-[#141414] underline underline-offset-4 hover:text-[#003e5b] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#141414] rounded"
          >
            Malt
            <span aria-hidden="true"> →</span>
          </a>
        </p>

        <div className="flex flex-wrap items-center gap-x-8 gap-y-3 pt-2">
          <span className="font-mono text-[11px] tracking-[2px] uppercase text-[#666]">Languages</span>
          {LANGUAGES.map((lang) => (
            <span key={lang.name} className="text-[15px] text-[#141414]">
              <span className="font-semibold">{lang.name}</span>
              <span className="text-[#666]"> — {lang.level}</span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
